import type { Tenant, List } from "@/payload-types";

/**
 * Default popular competition IDs per sport, in FansUnited native format.
 * A tenant's `livescore.popularCompetitions` list overrides these on a
 * per-sport basis — see `resolvePopularCompetitionIds` for the routing rules.
 */
const DEFAULT_POPULAR_IDS = {
    football: ["fb:c:3", "fb:c:5", "fb:c:6", "fb:c:7", "fb:c:8", "fb:c:27", "fb:c:28", "fb:c:41"],
    basketball: [
        "bb:c:1330", // NBA
        "bb:c:1331", // WNBA
        "bb:c:1096", // EuroLeague
    ],
    tennis: [
        // Grand Slams (M + W)
        "tn:c:1655", // Australian Open
        "tn:c:1641", // Australian Open (Women)
        "tn:c:2335", // Roland Garros
        "tn:c:2322", // Roland Garros (Women)
        "tn:c:2567", // Wimbledon
        "tn:c:2573", // Wimbledon (Women)
        "tn:c:3770", // US Open
        "tn:c:3799", // US Open (Women)
        // ATP Masters 1000
        "tn:c:3767", // Miami Masters
        "tn:c:3497", // Madrid Masters
        "tn:c:2775", // Rome Masters
        "tn:c:3771", // Cincinnati Masters
    ],
    hockey: [
        "hk:c:1499", // NHL
        "hk:c:1470", // KHL
        "hk:c:1511", // IIHF World Championship
    ],
    "american-football": [
        "af:c:4271", // NFL
    ],
} as const;

type Sport = keyof typeof DEFAULT_POPULAR_IDS;

/**
 * Native FansUnited competition IDs are prefixed by sport
 * (`fb:c:`, `bb:c:`, `tn:c:`, `hk:c:`, `af:c:`). This map lets us route each
 * ID in the tenant's CMS list to the right per-sport bucket.
 */
const PREFIX_TO_SPORT: Record<string, Sport> = {
    "fb:c:": "football",
    "bb:c:": "basketball",
    "tn:c:": "tennis",
    "hk:c:": "hockey",
    "af:c:": "american-football",
};

/** Auto-refresh interval for live match data. */
export const LIVESCORE_REFRESH_INTERVAL_MS = 30_000;

const SPORT_TO_PREFIX: Record<Sport, string> = {
    football: "fb:c:",
    basketball: "bb:c:",
    tennis: "tn:c:",
    hockey: "hk:c:",
    "american-football": "af:c:",
};

/**
 * The popular competition ids the livescore board's first stage asks the API for:
 * `resolvePopularCompetitionIds` narrowed to one sport by native prefix, or every
 * sport when no sport is given (the all-sports ticker). Never empty — each sport
 * falls back to its built-in defaults.
 */
export function popularCompetitionIdsForSport(tenant: Tenant | null | undefined, sport?: Sport): string[] {
    const all = resolvePopularCompetitionIds(tenant);
    if (!sport) return all;
    const prefix = SPORT_TO_PREFIX[sport];
    return all.filter((id) => id.startsWith(prefix));
}

/**
 * Merges the tenant's `popularCompetitions` CMS list with the built-in
 * defaults, on a per-sport basis. Behavior:
 *
 *   - IDs are routed to a sport by their native prefix (`fb:c:` → football,
 *     `bb:c:` → basketball, `tn:c:` → tennis, `hk:c:` → hockey, `af:c:` →
 *     american football). Unknown-prefix IDs are dropped with a `console.warn`
 *     so typos surface instead of silently breaking the Popular tab.
 *   - For each sport, curated IDs *replace* the defaults for that sport.
 *   - Sports absent from the CMS list keep their defaults. A tenant curating
 *     only basketball, for example, still gets football defaults.
 *
 * The client-side matcher in `views/livescore/utils/grouping.ts` is a plain
 * string compare against `group.competitionId`, so returning a single merged
 * array is enough — IDs from other sports naturally don't match the currently
 * fetched sport's groups.
 */
export function resolvePopularCompetitionIds(tenant: Tenant | null | undefined): string[] {
    const buckets: Record<Sport, string[]> = {
        football: [],
        basketball: [],
        tennis: [],
        hockey: [],
        "american-football": [],
    };

    const popularCompetitions = tenant?.features?.livescore?.popularCompetitions;
    if (popularCompetitions && typeof popularCompetitions === "object") {
        const list = popularCompetitions as List;
        const ids = (list.items ?? []).map((item) => item.entityId).filter(Boolean) as string[];
        for (const id of ids) {
            const prefix = Object.keys(PREFIX_TO_SPORT).find((p) => id.startsWith(p));
            if (!prefix) {
                console.warn(
                    `[resolvePopularCompetitionIds] Ignoring unknown competition ID "${id}" — expected prefix fb:c: / bb:c: / tn:c: / hk:c: / af:c:.`
                );
                continue;
            }
            buckets[PREFIX_TO_SPORT[prefix]].push(id);
        }
    }

    const forSport = (s: Sport): readonly string[] => (buckets[s].length > 0 ? buckets[s] : DEFAULT_POPULAR_IDS[s]);

    return [
        ...forSport("football"),
        ...forSport("basketball"),
        ...forSport("tennis"),
        ...forSport("hockey"),
        ...forSport("american-football"),
    ];
}
